import { makeAutoObservable } from "mobx";
import { SwipeUser } from "../models/api/SwipeUser";
import { ProgStatus } from "../models/utilTypes";
import ApiService from "../services/ApiService";
import LocalStorageService from "../services/LocalStorageService";
import { rootStore } from "../StApp";
import { LikesComputer } from "./interfaces/LikesComputer";

type SelectionMode = 'SWIPE' | 'STANDARD'

const SWIPE_BUFFER_KEY = 'swipe_buffer'
const SWIPE_SHOW_COUNT = 1
const STANDARD_SHOW_COUNT = 3


export class DateshipStore implements LikesComputer {

    private SHOW_COUNT = 3
    swipeBuffer: SwipeUser[] = []
    currentShownUsers: SwipeUser[] = []
    swipeLoadProg: ProgStatus = 'INIT'
    likedUids: string[] = []
    likesLeft = 25
    selectionMode: SelectionMode = 'STANDARD'


    constructor() {
        makeAutoObservable(this, { swipeBuffer: true })

        this.initSwipeBuffer()

        let localLikesLeft = LocalStorageService.getItem('likes_left')
        if (localLikesLeft)
            this.likesLeft = +localLikesLeft
        else
            this.updateLeftLikes(1)

    }

    updateSelectionMode(mode: SelectionMode) {
        this.selectionMode = mode
        if (mode == 'STANDARD')
            this.SHOW_COUNT = STANDARD_SHOW_COUNT
        else
            this.SHOW_COUNT = SWIPE_SHOW_COUNT
    }

    loadSwipeQueue() {

        if (this.swipeBuffer.length > 0) {

            if (this.swipeBuffer.length > 4) {

                this.updateSwipeState()
            }
            else {

                //fetch more swipe users remotely
                if (this.swipeBuffer.length >= this.SHOW_COUNT) {
                    this.updateSwipeState()
                } else {
                    this.fetchAndUpdateSwipeQueueFromRemote()

                }
            }
        }
        else {

            //fetch swipe users remotely
            //load them to buffer
            //load from buffer to state
            this.fetchAndUpdateSwipeQueueFromRemote()

        }
    }

    reloadLeftLikes(){
        let localLikesLeft = LocalStorageService.getItem('likes_left')
        if (localLikesLeft)
            this.likesLeft = +localLikesLeft
    }

    commitLike(uid: string): boolean {

        this.swipeBuffer = this.swipeBuffer.filter(item => item.uid != uid)
        this.updateSwipeBuffer()

        if (this.likesLeft < 1)
            return false

        ApiService.likeOrDump({
            like: true,
            swipeUid: uid,
            uid: rootStore.mainStore.uid!
        }).then(resp => {
            this.updateLeftLikes(resp.data.likesLeft)
        })

        this.loadNextBatch()
        return true

    }

    commitDump(uid: string) {

        this.swipeBuffer = this.swipeBuffer.filter(item => item.uid != uid)
        this.updateSwipeBuffer()

        ApiService.likeOrDump({
            like: false,
            swipeUid: uid,
            uid: rootStore.mainStore.uid!
        }).then(resp => {
            this.updateLeftLikes(resp.data.likesLeft)
        })

        this.loadNextBatch()

    }

    toggleLike(uid: string) {
        if (this.likedUids.includes(uid))
            this.dislikeUser(uid)
        else
            this.likeUser(uid)
    }

    private likeUser(uid: string) {
        if (this.likesLeft < 1)
            return
        this.likedUids = [...this.likedUids, uid]
        this.updateLeftLikes(this.likesLeft - 1)
    }

    private dislikeUser(uid: string) {
        this.likedUids = this.likedUids.filter(item => item !== uid)
    }

    confirmChoices() {

        let unlikedUids = (this.currentShownUsers.filter(item => this.likedUids.includes(item.uid) == false)).map(item => item.uid)
        this.swipeBuffer = this.swipeBuffer.filter(item => unlikedUids.includes(item.uid) == false)
        this.swipeBuffer = this.swipeBuffer.filter(item => this.likedUids.includes(item.uid) == false)
        this.updateSwipeBuffer()
        ApiService.registerLikeAndDump({
            auth: rootStore.mainStore.token,
            data: {
                uid: rootStore.mainStore.uid!,
                likedUids: this.likedUids,
                unLikedUids: unlikedUids
            }
        }).then(resp => {
            console.log(resp.data)
            this.updateLeftLikes(resp.data.likeLeft)

        })
        this.loadNextBatch()

    }

    private loadNextBatch() {
        this.likedUids = []
        this.updateSwipeState(true)
        this.updateSwipeBuffer()
    }

    private initSwipeBuffer() {
        let localData = localStorage.getItem(SWIPE_BUFFER_KEY)
        if (localData != null) {
            this.swipeBuffer = JSON.parse(localData) as SwipeUser[]
            //this.loadSwipeQueue()
        }

    }

    private updateSwipeState(force: boolean = false) {

        //If the users are shown currently, exit!
        if (this.currentShownUsers.length > 0 && !force)
            return

        //load swipe users from buffer to state and remove them from buffer
        this.currentShownUsers = this.swipeBuffer.slice(0, this.SHOW_COUNT)
        //this.swipeBuffer.splice(0, this.SHOW_COUNT)

        //TODO: update the swipe buffer and local storage
        //this.updateSwipeBuffer()
        //Check if new swipe buffer has min items
        if (this.swipeBuffer.length <= this.SHOW_COUNT) {
            this.fetchSwipeQueueRemotelySilently()
        }

    }

    private fetchSwipeQueueRemotelySilently() {

        this.swipeLoadProg = 'LOADING'

        ApiService.fetchSwipeQueue({
            auth: rootStore.mainStore.token,
            data: {
                uid: rootStore.mainStore.uid!
            }
        }).then(resp => {

            this.swipeLoadProg = 'SUCCESS'

            if (resp.data.userList.length > 0) {
                this.addToSwipeBuffer(resp.data.userList)
            }
        }).catch(e=> this.swipeLoadProg = 'INIT' )

    }

    private async fetchAndUpdateSwipeQueueFromRemote() {

        this.swipeLoadProg = 'LOADING'

        try {

            let resp = await ApiService.fetchSwipeQueue({
                auth: rootStore.mainStore.token,
                data: {
                    uid: rootStore.mainStore.uid!
                }
            })

            if (resp.data.userList.length > 0) {
                this.addToSwipeBuffer(resp.data.userList)
                this.updateSwipeState()
            }



        } catch (error) {
            console.error(error)
        } finally {
            this.swipeLoadProg = 'SUCCESS'
        }

    }

    private addToSwipeBuffer(swipeList: SwipeUser[]) {

        this.swipeBuffer = [...this.swipeBuffer, ...swipeList]
        localStorage.setItem(SWIPE_BUFFER_KEY, JSON.stringify(this.swipeBuffer))
    }

    private updateSwipeBuffer() {
        localStorage.setItem(SWIPE_BUFFER_KEY, JSON.stringify(this.swipeBuffer))
    }

    private updateLeftLikes(value: number) {
        this.likesLeft = value
        LocalStorageService.updateStorage('likes_left', value)
    }



}